//imports
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { disableIdleLockSwitch, enableIdleLockSwitch, displayNotice, RootState, AppDispatch } from "../../../../globals";
import { StudentProgressRowDTO, StudentProgressSummaryDTO } from "../../../../models/StudentProgressDTO";
import {
	AttendanceModalState, GradesModalState, IdOption, PROGRESS_FILTERS, ProgressChip, ProgressTab, ProgressFilter, ScheduleOption,
	SIN_MAESTRA, SIN_MAESTRA_ID, StructuredFilterOptions, StructuredFilters, WhatsappModalState
} from "./interfaces";
import { horarioBonito } from "../../../../utils/horarioBonito";
import S from "./strings";

//services
import HttpManager from "../../../../services/HttpManager";

//view
import StudentProgressView from "./studentProgressView";

//messages
const noCampuses = "No hay planteles registrados o asignados al rol en uso por el usuario, por lo que el acceso a este módulo permanecerá deshabilitado.";
const initError = "La búsqueda inicial de datos no pudo ser completada.";
const attendancesError = "No se pudieron cargar las asistencias.";

//constants
const PAGE_SIZE = 50;
/** Al volver atrás (p. ej. desde Estudiantes) se reusa lo ya cargado si tiene menos de esto. */
const CACHE_MS = 5 * 60 * 1000;

const resumenVacio: StudentProgressSummaryDTO = {
	total: 0, rotating: 0, composite: 0, withAlert: 0, absent: 0, absentWeek: 0, overHours: 0, completed: 0, graduating: 0
};

const filtrosVacios: StructuredFilters = { mentorIds: [], courseIds: [], periodIds: [], scheduleIds: [] };

const whatsappCerrado: WhatsappModalState = { row: null, template: "" };

const asistenciasCerrado: AttendanceModalState = { row: null, loading: false, detail: null, error: null };

const notasCerrado: GradesModalState = { row: null, loading: false, detail: null, error: null };

/*
	Lo último cargado, por plantel e interruptores: al regresar a la pantalla (volver atrás)
	no se vuelve a pedir si sigue fresco. Un cambio en Por Reponer lo invalida.
*/
let cacheProgreso: { llave: string; rows: StudentProgressRowDTO[]; summary: StudentProgressSummaryDTO; en: number } | null = null;

const maestraIdDe = (row: StudentProgressRowDTO): number => row.mentorId == null ? SIN_MAESTRA_ID : Number(row.mentorId);
const maestraDe = (row: StudentProgressRowDTO): string => row.mentorName || SIN_MAESTRA;

const ESTADO_HORARIO: { [id: number]: string } = { 0: "Sin iniciar", 1: "Activo", 2: "Fusionado", 3: "Terminado", 4: "Cerrando" };

const tieneAlerta = (row: StudentProgressRowDTO, code: string): boolean =>
	Array.isArray(row.alerts) && row.alerts.some(a => a.code === code);

const pasaFiltro = (row: StudentProgressRowDTO, filter: ProgressFilter): boolean => {
	switch (filter) {
		case "rotativos": return row.isRotating === true;
		case "compuestos": return row.isComposite === true;
		case "sinAsistir": return tieneAlerta(row, "absent");
		case "faltoSemana": return tieneAlerta(row, "absent_week");
		case "horasDeMas": return tieneAlerta(row, "over_hours");
		case "cubierto": return tieneAlerta(row, "completed");
		case "porGraduarse": return row.graduatingSoon === true || tieneAlerta(row, "graduating");
		case "bajoMinimo": return tieneAlerta(row, "below_minimum");
		case "bloqueSinSiguiente": return tieneAlerta(row, "block_done_no_next") || tieneAlerta(row, "block_missing");
		default: return true;
	}
};

/* Filtros por id; lista vacía = todos. */
const pasaEstructurados = (row: StudentProgressRowDTO, f: StructuredFilters): boolean =>
	(f.mentorIds.length === 0 || f.mentorIds.includes(maestraIdDe(row)))
	&& (f.courseIds.length === 0 || f.courseIds.includes(Number(row.courseId)))
	&& (f.periodIds.length === 0 || f.periodIds.includes(Number(row.periodId)))
	&& (f.scheduleIds.length === 0 || f.scheduleIds.includes(Number(row.scheduleId)));

/* Sin acentos ni mayusculas, para que "Jose" encuentre a "José". */
const normalizar = (texto: any): string =>
	String(texto || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/*
	Orden: apellido y luego nombre, sin acentos. Si la fila trae lastName/firstName se ordena aquí;
	si solo viene studentName ("nombre apellido"), se respeta el orden del servidor, que ya viene
	por u.last_name, u.first_name (partir el nombre por palabras confunde apellidos compuestos).
*/
const compararFilas = (orden: Map<number, number>) => (a: StudentProgressRowDTO, b: StudentProgressRowDTO): number => {
	if ((a.lastName || a.firstName) && (b.lastName || b.firstName)) {
		return normalizar(a.lastName).localeCompare(normalizar(b.lastName), "es")
			|| normalizar(a.firstName).localeCompare(normalizar(b.firstName), "es");
	}
	return (orden.get(a.enrollmentId) || 0) - (orden.get(b.enrollmentId) || 0);
};

const opcionesPorId = (pares: [number, string][]): IdOption[] => {
	const m = new Map<number, string>();
	pares.forEach(([id, name]) => { if (!m.has(id)) m.set(id, name); });
	return Array.from(m.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "es"));
};

const csvCelda = (valor: any): string => {
	const texto = valor == null ? "" : String(valor);
	return /[",;\n\r]/.test(texto) ? `"${texto.replace(/"/g, "\"\"")}"` : texto;
};

const notaTexto = (row: StudentProgressRowDTO): string => {
	const g = row.grade;
	if (!g || g.final === null || g.final === undefined || isNaN(Number(g.final))) return S.gradeNA;
	return Number(g.final).toFixed(1);
};

/* ---- filtros en la URL (volver atrás los restaura) ---- */
const leerIds = (sp: URLSearchParams, llave: string): number[] =>
	String(sp.get(llave) || "").split(",").filter(v => v.trim() !== "").map(v => Number(v)).filter(v => Number.isInteger(v) && v >= 0);

const idsTexto = (ids: number[]): string | null => ids.length ? ids.join(",") : null;

const StudentProgressController = () => {

	//init dispatch
	const dispatch = useDispatch<AppDispatch>();
	const [searchParams, setSearchParams] = useSearchParams();

	//init http
	const http = HttpManager.getInstance();

	//useSelector
	const sessionToken: string | null = useSelector((state: RootState) => state.sessionToken.value);
	const idleLocked: boolean = useSelector((state: RootState) => state.idleLockSwitch.value);
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	//El plantel es el que muestra el encabezado de WAPP (campusRef[currentUserCampus]);
	//la sesion v2 (sessionState) solo entra si ese aun no esta.
	const currentUserCampus: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const campusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const currentCampusId: number = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampusId);
	const currentCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);

	const campusHeader: any = campusRef && currentUserCampus != null ? campusRef[currentUserCampus] : null;
	const campusId: number = (campusHeader && campusHeader.id) || currentCampusId || 0;
	const campusName: string = (campusHeader && campusHeader.name) || (currentCampus && currentCampus.name) || "";
	//Exportar: solo superadmin (rol 0), igual que Bodegas lee el rol en uso.
	const canExport: boolean = currentUser?.currentRole === "superAdmin";

	/* La URL manda: pestaña, chip, filtros por id, búsqueda, interruptores y página. */
	const tab: ProgressTab = searchParams.get("pestana") === "reponer" ? "reponer" : "progreso";
	const filtroUrl = searchParams.get("filtro") as ProgressFilter | null;
	const filter: ProgressFilter = filtroUrl && PROGRESS_FILTERS.includes(filtroUrl) ? filtroUrl : "todos";
	const includeInactive: boolean = searchParams.get("noActivos") === "1";
	const includeInactiveStudents: boolean = searchParams.get("noActivas") === "1";
	const search: string = searchParams.get("q") || "";
	const page: number = Math.max(1, Number(searchParams.get("pagina")) || 1);
	const structured: StructuredFilters = useMemo(() => ({
		mentorIds: leerIds(searchParams, "maestras"),
		courseIds: leerIds(searchParams, "cursos"),
		periodIds: leerIds(searchParams, "periodos"),
		scheduleIds: leerIds(searchParams, "horarios")
	}), [searchParams]);

	const escribirUrl = (cambios: { [llave: string]: string | null }, reemplazar: boolean = false) => {
		const siguiente = new URLSearchParams(searchParams);
		Object.keys(cambios).forEach(llave => {
			const valor = cambios[llave];
			if (valor === null || valor === "") siguiente.delete(llave); else siguiente.set(llave, valor);
		});
		setSearchParams(siguiente, { replace: reemplazar });
	};

	//useState
	const [sectionLoaded, setSectionLoaded] = useState<boolean>(false);
	const [rows, setRows] = useState<StudentProgressRowDTO[]>([]);
	const [summaryServer, setSummaryServer] = useState<StudentProgressSummaryDTO>(resumenVacio);
	const [attendance, setAttendance] = useState<AttendanceModalState>(asistenciasCerrado);
	const [grades, setGrades] = useState<GradesModalState>(notasCerrado);
	const [profileRow, setProfileRow] = useState<StudentProgressRowDTO | null>(null);
	const [whatsapp, setWhatsapp] = useState<WhatsappModalState>(whatsappCerrado);
	const [selected, setSelected] = useState<number[]>([]);
	const [bulkOpen, setBulkOpen] = useState<boolean>(false);
	const currencyCode: string = (campusHeader && campusHeader.company && campusHeader.company.currency && campusHeader.company.currency.code) || "";
	const recoveryOpen: number = useMemo(() => rows.reduce((acc, r) => acc + (Number(r.recoveryOpen) || 0), 0), [rows]);

	//functions
	const displayError = (message: string) => {
		dispatch(
			displayNotice({
				cornerClose: false,
				message: message,
				heading:
					<h3 style={{ color: "#A8730A", display: "inline-block" }}>
						Error
					</h3>
			})
		);
	};

	/* Se carga solo con los dos interruptores (horarios / alumnas no activas); chips, filtros y buscador filtran en el navegador. */
	const getProgress = async (forzar: boolean = false) => {
		if (!campusId) return;
		const llave = `${campusId}|${includeInactive ? 1 : 0}|${includeInactiveStudents ? 1 : 0}`;

		if (!forzar && cacheProgreso && cacheProgreso.llave === llave && Date.now() - cacheProgreso.en < CACHE_MS) {
			setRows(cacheProgreso.rows);
			setSummaryServer(cacheProgreso.summary);
			setSectionLoaded(true);
			return;
		}

		try {
			dispatch(enableIdleLockSwitch());
			setRows([]);
			const result = await http.studentProgressService.getStudentProgress(campusId, { includeInactive, includeInactiveStudents });

			if (result.status == 200 || result.status == 204) {
				const data: any = result.data || {};
				const filas: StudentProgressRowDTO[] = Array.isArray(data.rows) ? data.rows : [];
				const resumen: StudentProgressSummaryDTO = { ...resumenVacio, ...(data.summary || {}) };
				cacheProgreso = { llave, rows: filas, summary: resumen, en: Date.now() };
				setRows(filas);
				setSummaryServer(resumen);
				setSectionLoaded(true);
			} else {
				displayError(initError);
			}
		} catch (error) {
			console.log(error);
			displayError(initError);
		} finally {
			dispatch(disableIdleLockSwitch());
		}
	};

	/* Filas que pasan maestra/curso/periodo/horario y el buscador: sobre estas cuentan el encabezado y los chips. */
	const filasBase: StudentProgressRowDTO[] = useMemo(() => {
		const q = normalizar(search.trim());

		return rows
			.filter(r => pasaEstructurados(r, structured))
			.filter(r => {
				if (!q) return true;
				const texto = normalizar(
					[r.studentName, r.identityCode, r.phone, r.courseName, r.courseCode, r.levelName, r.periodName, r.scheduleName, horarioBonito(r.scheduleName), r.mentorName]
						.filter(Boolean)
						.join(" ")
				);
				return texto.includes(q);
			});
	}, [rows, structured, search]);

	/*
		Las cuentas de los chips y del encabezado salen de las filas filtradas (no del
		resumen del backend), para que coincidan siempre con lo que se ve al pulsar cada uno.
	*/
	const chips: ProgressChip[] = useMemo(() => {
		const cuenta = (llave: ProgressFilter) => filasBase.filter(r => pasaFiltro(r, llave)).length;
		return [
			{ llave: "todos", texto: S.chipAll, cuenta: filasBase.length },
			{ llave: "rotativos", texto: S.chipRotating, cuenta: cuenta("rotativos") },
			{ llave: "compuestos", texto: S.chipComposite, cuenta: cuenta("compuestos") },
			{ llave: "sinAsistir", texto: S.chipAbsent, cuenta: cuenta("sinAsistir") },
			{ llave: "faltoSemana", texto: S.chipAbsentWeek, cuenta: cuenta("faltoSemana") },
			{ llave: "porGraduarse", texto: S.chipGraduating, cuenta: cuenta("porGraduarse") },
			{ llave: "bajoMinimo", texto: S.chipBelowMinimum, cuenta: cuenta("bajoMinimo") },
			{ llave: "bloqueSinSiguiente", texto: S.chipBlockNoNext, cuenta: cuenta("bloqueSinSiguiente") },
			{ llave: "horasDeMas", texto: S.chipOverHours, cuenta: cuenta("horasDeMas") },
			{ llave: "cubierto", texto: S.chipCompleted, cuenta: cuenta("cubierto") }
		];
	}, [filasBase]);

	const summary: StudentProgressSummaryDTO = useMemo(() => ({
		...summaryServer,
		total: filasBase.length,
		rotating: filasBase.filter(r => r.isRotating).length,
		composite: filasBase.filter(r => r.isComposite).length,
		withAlert: filasBase.filter(r => Array.isArray(r.alerts) && r.alerts.length > 0).length,
		graduating: filasBase.filter(r => pasaFiltro(r, "porGraduarse")).length
	}), [filasBase, summaryServer]);

	/* Opciones de maestra, curso, periodo y horario (por id): cada una se acota a lo elegido antes. */
	const structuredOptions: StructuredFilterOptions = useMemo(() => {
		const porMaestra = structured.mentorIds.length ? rows.filter(r => structured.mentorIds.includes(maestraIdDe(r))) : rows;
		const porCurso = structured.courseIds.length ? porMaestra.filter(r => structured.courseIds.includes(Number(r.courseId))) : porMaestra;
		const porPeriodo = structured.periodIds.length ? porCurso.filter(r => structured.periodIds.includes(Number(r.periodId))) : porCurso;

		//Los horarios solo se listan con cursos o maestras elegidos: sin acotar serían cientos de chips iguales.
		const horarios = new Map<number, ScheduleOption>();
		if (structured.courseIds.length || structured.mentorIds.length) {
			porPeriodo.forEach(r => {
				if (r.scheduleId != null && !horarios.has(r.scheduleId)) {
					horarios.set(r.scheduleId, { id: r.scheduleId, name: horarioBonito(r.scheduleName) || `Horario ${r.scheduleId}`, level: r.levelName || "", period: r.periodName || "" });
				}
			});
		}
		//Si dos horarios del mismo curso se llaman igual, se distingue por módulo
		const repetidos = new Set<string>();
		const vistos = new Set<string>();
		horarios.forEach(h => { if (vistos.has(h.name)) repetidos.add(h.name); vistos.add(h.name); });
		//Repetidos: primero se agrega el módulo; si siguen iguales, también el periodo
		const conModulo = Array.from(horarios.values()).map(h => repetidos.has(h.name) && h.level ? { ...h, name: `${h.name} · ${h.level}` } : h);
		const aunRepetidos = new Set<string>();
		const vistos2 = new Set<string>();
		conModulo.forEach(h => { if (vistos2.has(h.name)) aunRepetidos.add(h.name); vistos2.add(h.name); });
		const schedules = conModulo
			.map(h => aunRepetidos.has(h.name) && h.period ? { ...h, name: `${h.name} · ${h.period}` } : h)
			.sort((a, b) => a.name.localeCompare(b.name, "es"));

		//"Sin Maestra" va al final, no entre la S de los apellidos.
		const maestras = opcionesPorId(rows.filter(r => r.mentorId != null).map(r => [Number(r.mentorId), maestraDe(r)]));
		if (rows.some(r => r.mentorId == null)) maestras.push({ id: SIN_MAESTRA_ID, name: SIN_MAESTRA });

		return {
			mentors: maestras,
			courses: opcionesPorId(porMaestra.map(r => [Number(r.courseId), r.courseName])),
			periods: opcionesPorId(porCurso.map(r => [Number(r.periodId), r.periodName])),
			schedules
		};
	}, [rows, structured]);

	const ordenServidor: Map<number, number> = useMemo(() => new Map(rows.map((r, i) => [r.enrollmentId, i])), [rows]);

	const filasFiltradas: StudentProgressRowDTO[] = useMemo(() =>
		filasBase.filter(r => pasaFiltro(r, filter)).sort(compararFilas(ordenServidor)),
		[filasBase, filter, ordenServidor]);

	const pageCount: number = Math.max(1, Math.ceil(filasFiltradas.length / PAGE_SIZE));
	const paginaActual: number = Math.min(page, pageCount);
	const filasPagina: StudentProgressRowDTO[] = filasFiltradas.slice((paginaActual - 1) * PAGE_SIZE, paginaActual * PAGE_SIZE);

	const onFilter = (nuevo: ProgressFilter) => escribirUrl({ filtro: nuevo === "todos" ? null : nuevo, pagina: null });

	const onToggleInactive = () => escribirUrl({ noActivos: includeInactive ? null : "1", maestras: null, cursos: null, periodos: null, horarios: null, pagina: null });

	const onToggleInactiveStudents = () => escribirUrl({ noActivas: includeInactiveStudents ? null : "1", maestras: null, cursos: null, periodos: null, horarios: null, pagina: null });

	//Lo elegido abajo (curso, periodo, horario) se limpia al cambiar lo de arriba: ya no aplica.
	const onMentors = (ids: number[]) => escribirUrl({ maestras: idsTexto(ids), cursos: null, periodos: null, horarios: null, pagina: null });

	const onCourses = (ids: number[]) => escribirUrl({ cursos: idsTexto(ids), periodos: null, horarios: null, pagina: null });

	const onPeriods = (ids: number[]) => escribirUrl({ periodos: idsTexto(ids), horarios: null, pagina: null });

	const onToggleSchedule = (scheduleId: number) => {
		const ids = structured.scheduleIds.includes(scheduleId)
			? structured.scheduleIds.filter(id => id !== scheduleId)
			: [...structured.scheduleIds, scheduleId];
		escribirUrl({ horarios: idsTexto(ids), pagina: null });
	};

	const onClearStructured = () => escribirUrl({ maestras: null, cursos: null, periodos: null, horarios: null, pagina: null });

	//El buscador reemplaza la entrada del historial: una tecla no es un paso al que volver.
	const onSearch = (valor: string) => escribirUrl({ q: valor || null, pagina: null }, true);

	const onPage = (nueva: number) => escribirUrl({ pagina: nueva <= 1 ? null : String(nueva) });

	const onTab = (nueva: ProgressTab) => escribirUrl({ pestana: nueva === "reponer" ? "reponer" : null });

	/* CSV de lo que se ve (con filtros y busqueda aplicados), separado por coma, UTF-8 con BOM para Excel. */
	const onExport = () => {
		if (!canExport) return;
		const encabezados = ["Alumna", "Correlativo", "Teléfono", "Estatus matrícula", "Curso", "Módulo", "Periodo", "Horario", "Maestra", "Estado del horario", "Fin del horario",
			"Tipo", "Compuesto", "Horarios autorizados", "Horas tomadas", "Horas contratadas", "Sesiones tomadas", "Sesiones totales",
			"% Asistencia", "Mínimo %", "Inasistencias", "Repuestas", "Última asistencia", "Semanas sin ir", "Alerta", "Calificación", "Estado de nota"];

		const estadoNota = (r: StudentProgressRowDTO): string => {
			const g = r.grade;
			if (!g || g.final === null || g.final === undefined || g.status === "sin_notas") return S.gradeNoGrades;
			return g.status === "aprobado" ? S.gradeApproved : S.gradeAtRisk;
		};

		const lineas = filasFiltradas.map(r => [
			r.studentName,
			r.identityCode,
			r.phone || "",
			r.enrollmentStatus || "",
			r.courseName,
			r.levelName,
			r.periodName,
			horarioBonito(r.scheduleName),
			maestraDe(r),
			ESTADO_HORARIO[r.scheduleStatusId] || "",
			r.scheduleEndDate || "",
			r.isRotating ? "Rotativo" : "Fijo",
			r.isComposite ? "Sí" : "No",
			(r.authorizedSchedules || []).map(s => horarioBonito(s.name)).join(" / "),
			r.takenHours,
			r.entitledHours,
			r.sessionsTaken,
			r.sessionsTotal,
			r.attendancePct === null || r.attendancePct === undefined ? "" : r.attendancePct,
			r.minAttendancePct ?? "",
			r.missedSessions ?? "",
			r.sessionsRecovered ?? 0,
			r.lastPresent || S.never,
			r.weeksAbsent,
			(r.alerts || []).length === 0 ? S.upToDate : r.alerts.map(a => a.label).join(" / "),
			notaTexto(r),
			estadoNota(r)
		].map(csvCelda).join(","));

		const contenido = "﻿" + [encabezados.map(csvCelda).join(","), ...lineas].join("\r\n");
		const blob = new Blob([contenido], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const enlace = document.createElement("a");
		enlace.href = url;
		enlace.download = `Progreso Académico${campusName ? " - " + campusName : ""}.csv`;
		document.body.appendChild(enlace);
		enlace.click();
		document.body.removeChild(enlace);
		URL.revokeObjectURL(url);
	};

	/* Calificaciones: el detalle (categorías y evaluaciones) se pide al abrir el modal. */
	const onOpenGrades = async (row: StudentProgressRowDTO) => {
		if (!campusId) return;
		setGrades({ row, loading: true, detail: null, error: null });

		try {
			const result = await http.studentProgressService.getGrades(campusId, row.enrollmentId);
			const detail: any = result.data || null;
			setGrades(prev => prev.row && prev.row.enrollmentId === row.enrollmentId
				? { ...prev, loading: false, detail: detail && typeof detail === "object" ? { ...detail, categories: Array.isArray(detail.categories) ? detail.categories : [] } : null, error: detail ? null : S.gradesError }
				: prev);
		} catch (error) {
			console.log(error);
			setGrades(prev => prev.row && prev.row.enrollmentId === row.enrollmentId
				? { ...prev, loading: false, error: S.gradesError }
				: prev);
		}
	};

	const onCloseGrades = () => setGrades(notasCerrado);

	//Ficha de la alumna en ventana: no se navega fuera de la pantalla.
	const onOpenProfile = (row: StudentProgressRowDTO) => setProfileRow(row);
	const onCloseProfile = () => setProfileRow(null);

	/* Asistencias de la alumna en esa matricula: se piden al abrir el modal, no se guardan entre filas. */
	const onOpenAttendances = async (row: StudentProgressRowDTO) => {
		if (!campusId) return;
		setAttendance({ row, loading: true, detail: null, error: null });

		try {
			const result = await http.studentProgressService.getAttendances(campusId, row.enrollmentId);
			const detail: any = result.data || null;
			setAttendance(prev => prev.row && prev.row.enrollmentId === row.enrollmentId
				? { ...prev, loading: false, detail: detail && Array.isArray(detail.classes) ? detail : null, error: detail ? null : attendancesError }
				: prev);
		} catch (error) {
			console.log(error);
			setAttendance(prev => prev.row && prev.row.enrollmentId === row.enrollmentId
				? { ...prev, loading: false, error: attendancesError }
				: prev);
		}
	};

	const onCloseAttendances = () => setAttendance(asistenciasCerrado);

	const onOpenWhatsapp = (row: StudentProgressRowDTO) => setWhatsapp({ ...whatsappCerrado, row });

	const onCloseWhatsapp = () => setWhatsapp(whatsappCerrado);

	const onPickTemplate = (key: string) => setWhatsapp(prev => ({ ...prev, template: prev.template === key ? "" : key }));

	/* WhatsApp a varias: la selección es por alumna (una alumna con dos matrículas recibe un solo mensaje). */
	const onToggleSelected = (studentId: number) =>
		setSelected(prev => prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]);

	const onSelectMany = (studentIds: number[], checked: boolean) =>
		setSelected(prev => checked ? Array.from(new Set([...prev, ...studentIds])) : prev.filter(id => !studentIds.includes(id)));

	const onClearSelected = () => setSelected([]);

	const onOpenBulk = () => { if (selected.length > 0) setBulkOpen(true); };

	const onCloseBulk = () => setBulkOpen(false);

	//useEffect
	useEffect(() => {
		if (sessionToken) {
			campusId ? getProgress() : displayError(noCampuses);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, includeInactive, includeInactiveStudents]);

	//Al cambiar de plantel se cierran los modales y se olvida la selección.
	useEffect(() => {
		setWhatsapp(whatsappCerrado);
		setGrades(notasCerrado);
		setProfileRow(null);
		setAttendance(asistenciasCerrado);
		setSelected([]);
		setBulkOpen(false);
	}, [campusId]);

	return (
		<StudentProgressView
			sectionLoaded={sectionLoaded}
			campusName={campusName}
			idleLocked={idleLocked}
			summary={summary}
			chips={chips}
			filter={filter}
			onFilter={onFilter}
			includeInactive={includeInactive}
			onToggleInactive={onToggleInactive}
			includeInactiveStudents={includeInactiveStudents}
			onToggleInactiveStudents={onToggleInactiveStudents}
			structured={structured}
			structuredOptions={structuredOptions}
			onMentors={onMentors}
			onCourses={onCourses}
			onPeriods={onPeriods}
			onToggleSchedule={onToggleSchedule}
			onClearStructured={onClearStructured}
			search={search}
			onSearch={onSearch}
			rows={filasPagina}
			totalFiltered={filasFiltradas.length}
			page={paginaActual}
			pageCount={pageCount}
			pageSize={PAGE_SIZE}
			onPage={onPage}
			canExport={canExport}
			onExport={onExport}
			grades={grades}
			onOpenGrades={onOpenGrades}
			onCloseGrades={onCloseGrades}
			profileRow={profileRow}
			onOpenProfile={onOpenProfile}
			onCloseProfile={onCloseProfile}
			attendance={attendance}
			onOpenAttendances={onOpenAttendances}
			onCloseAttendances={onCloseAttendances}
			whatsapp={whatsapp}
			onOpenWhatsapp={onOpenWhatsapp}
			onCloseWhatsapp={onCloseWhatsapp}
			onPickTemplate={onPickTemplate}
			selected={selected}
			onToggleSelected={onToggleSelected}
			onSelectMany={onSelectMany}
			onClearSelected={onClearSelected}
			bulkOpen={bulkOpen}
			onOpenBulk={onOpenBulk}
			onCloseBulk={onCloseBulk}
			campusId={campusId}
			currencyCode={currencyCode}
			tab={tab}
			onTab={onTab}
			recoveryOpen={recoveryOpen}
			onRecoveriesChanged={() => getProgress(true)}
		/>
	);
}

export default StudentProgressController
